import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inviteApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  storeBabyKey,
  encryptKeyWithPublicKey,
  importPublicKey,
  decryptBabyKeyFromInvite,
  sessionKeys,
} from '../crypto';

interface InviteDetails {
  id: string;
  inviterName: string;
  role: string;
  expiresAt: string;
  email: string | null;
  encryptedBabyKey: string;
  babyId: string;
}

export default function InviteAccept() {
  const { token } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Fetch invite details
  const { data: invite, isLoading, error: fetchError } = useQuery({
    queryKey: ['invite', token],
    queryFn: async () => {
      if (!token) throw new Error('No invite token');
      const response = await inviteApi.getByToken(token);
      return response.data as InviteDetails;
    },
    enabled: !!token,
    retry: false,
  });

  // Accept invite mutation
  const acceptMutation = useMutation({
    mutationFn: async (encryptedBabyKey: string) => {
      if (!token) throw new Error('No invite token');
      const response = await inviteApi.accept(token, { encryptedBabyKey });
      return response.data;
    },
    onSuccess: () => {
      setAccepted(true);
    },
    onError: (err) => {
      setError((err as Error).message || 'Failed to accept invite');
    },
  });

  const handleAccept = async () => {
    if (!user || !invite || !token) return;

    setIsAccepting(true);
    setError(null);

    try {
      // Decrypt the baby key using the invite token
      // The key was encrypted by the inviter using a key derived from this token
      const babyAESKey = await decryptBabyKeyFromInvite(invite.encryptedBabyKey, token);

      // Re-encrypt with our public key for permanent storage
      const publicKey = await importPublicKey(user.publicKey);
      const encryptedBabyKey = await encryptKeyWithPublicKey(babyAESKey, publicKey);

      // Accept the invite (this stores the key on the server)
      await acceptMutation.mutateAsync(encryptedBabyKey);

      // Store the key locally in IndexedDB for persistence
      await storeBabyKey(invite.babyId, encryptedBabyKey);

      // Also add to session memory so it's immediately available
      sessionKeys.setBabyKey(invite.babyId, babyAESKey);

      // Invalidate babies query so dashboard refreshes the list
      queryClient.invalidateQueries({ queryKey: ['babies'] });
    } catch (err) {
      console.error('Failed to accept invite:', err);
      setError((err as Error).message || 'Failed to accept invite');
    } finally {
      setIsAccepting(false);
    }
  };

  // Check if invite requires specific email
  const emailMismatch = invite?.email && user && invite.email.toLowerCase() !== user.email.toLowerCase();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (fetchError || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <span className="text-6xl">❌</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Invalid Invite
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            This invite link is invalid, has expired, or has already been used.
          </p>
          <Link to="/" className="btn-primary mt-6 inline-block">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <span className="text-6xl">🎉</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            Invite Accepted!
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            You now have access to the baby's profile.
          </p>
          <Link to="/app/dashboard" className="btn-primary mt-6 inline-block">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <span className="text-6xl">👶</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            You've Been Invited!
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {invite.inviterName} has invited you to help track their baby on myTotTot.
          </p>
        </div>

        <div className="card p-6">
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Role: {invite.role === 'MEMBER' ? 'Caregiver' : invite.role}
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                {error}
              </div>
            )}

            {!user ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  Please log in or create an account to accept this invite.
                </p>
                <div className="flex gap-3">
                  <Link
                    to={`/login?redirect=/invite/${token}`}
                    className="btn-primary flex-1 text-center"
                  >
                    Log In
                  </Link>
                  <Link
                    to={`/register?redirect=/invite/${token}`}
                    className="btn-secondary flex-1 text-center"
                  >
                    Register
                  </Link>
                </div>
              </div>
            ) : emailMismatch ? (
              <div className="space-y-3">
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg text-sm">
                  This invite was sent to {invite.email}, but you're logged in as {user.email}.
                  Please log in with the correct account.
                </div>
                <Link to="/login" className="btn-secondary w-full text-center block">
                  Log In with Different Account
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  Logged in as {user.email}
                </p>
                <button
                  onClick={handleAccept}
                  disabled={isAccepting}
                  className="btn-primary w-full"
                >
                  {isAccepting ? 'Accepting...' : 'Accept Invite'}
                </button>
              </div>
            )}

            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              Expires: {new Date(invite.expiresAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
